// ConfigModal: lets the user pick a layout template and assign widgets to
// panels. Widgets grouped into the same panel render as tabs (handled by
// WidgetPanel). Within each view, a widget lives in exactly one panel;
// handleAssignWidget moves it when reassigned.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDashboardConfig, { getDashboardViewDefault } from '../../Store/dashboardConfigStore';
import { TEMPLATE_LIST, getTemplate, AREA_NAMES } from './layoutTemplates';
import { getAssignableWidgets, getWidget } from './widgetRegistry';
import { DASHBOARD_VIEWS, DASHBOARD_VIEW_LIST } from './dashboardViews';
import { forceResyncChainSnapshots } from '../../App';
import '../../Styles/configModal.css';

// Keyed by the reason strings forceResyncChainSnapshots can actually return.
// Since the transport became a websocket these come from three places: the
// client's own failures (disconnected, timeout, busy, not_authenticated,
// stopped), the server's replay codes lowercased by chainSnapshotClient
// (unavailable, failed), and payload validation (invalid_response, empty).
// An unmapped reason degrades to a bare "Resync failed.", which tells the user
// nothing actionable, so anything reachable belongs here.
const RESYNC_ERROR_MESSAGES = {
  // No usable connection to the service.
  no_connection: 'Not connected to the chain snapshot service.',
  disconnected: 'Lost the connection to the chain snapshot service. Try again.',
  not_authenticated: 'Still signing in to the chain snapshot service. Try again in a moment.',
  stopped: 'Resync was cancelled.',

  // The service answered, but not usefully.
  timeout: 'The chain snapshot service did not respond in time.',
  busy: 'A resync is already running. Wait for it to finish.',
  unavailable: 'Chain snapshot service is unavailable.',
  failed: 'Chain snapshot service could not build the replay payload.',
  invalid_response: 'Chain snapshot service returned an unexpected response.',
  no_sources: 'No replay sources are available for that trading session.',
  unsupported_schema: 'The replay service does not support this dashboard schema.',
  non_trading_date: 'The selected date is not a trading session.',
  replay_error: 'The replay service could not complete the recovery request.',
  empty: 'Chain snapshot service has no data for today yet.',
  incomplete_transfer: 'The replay transfer arrived incomplete; nothing was changed.',

  // Membership and session outcomes, unchanged by the transport swap.
  unauthorized: 'Membership check failed - reload the page and try again.',
  session_rolled: 'The trading session changed mid-resync. Try again.',
  prior_session: 'No recoverable minutes for the session being replayed; replay was left unchanged.',
  replay_exited: 'Replay ended before recovery completed; replay data was left unchanged.',
  persist_failed: 'Saving failed, so the rest will be lost on reload.',
};

const SHOW_RESYNC_CHART_DATA = false;

// persist_failed still recovered minutes, so it appends to the count rather
// than replacing it -- the other reasons recovered nothing worth reporting.
function replayResyncSummary(result) {
  const hydration = result.replayHydration;
  let replaySummary;
  if (hydration && hydration !== 'ok' && hydration !== 'persist_failed') {
    replaySummary = `Chart snapshots resynced. ${RESYNC_ERROR_MESSAGES[hydration] || 'Replay recovery failed.'}`;
  } else {
    const recovered = result.replayRecovered ?? 0;
    const gaps = result.replayGaps ?? 0;
    const before = result.replayChainMinutesBefore;
    const after = result.replayChainMinutesAfter;
    const timelineSummary = Number.isInteger(before) && Number.isInteger(after)
      ? before === after
        ? ` Chain timeline: ${after} minute${after === 1 ? '' : 's'} (unchanged).`
        : ` Chain timeline: ${before} to ${after} minutes.`
      : '';
    replaySummary = `Replay data refreshed.${timelineSummary} New chain frames saved: ${recovered}. Gaps remaining: ${gaps}.`;
    if (hydration === 'persist_failed') {
      replaySummary = `${replaySummary} ${RESYNC_ERROR_MESSAGES.persist_failed}`;
    }
  }

  if (!result.indexHydration) return replaySummary;
  if (result.indexHydration.status === 'available') {
    return `${replaySummary} SPX price history resynced.`;
  }
  if (result.indexHydration.status === 'retained') {
    return `${replaySummary} Retained SPX price history remains available, but refresh did not complete.`;
  }
  if (result.indexHydration.status === 'live') {
    return `${replaySummary} SPX live data is updating, but retained history was not confirmed.`;
  }
  return `${replaySummary} SPX price history is unavailable.`;
}

function TemplateThumb({ template, active, disabled, onClick }) {
  return (
    <button
      type="button"
      className={`cfg-thumb ${active ? 'is-active' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={template.label}
      aria-label={`Use ${template.label} layout`}
      aria-pressed={active}
    >
      <div className={`cfg-thumb__grid ${template.className}`}>
        {AREA_NAMES.slice(0, template.panelCount).map((a) => (
          <span key={a} className="cfg-thumb__cell" style={{ gridArea: a }} />
        ))}
      </div>
      <span className="cfg-thumb__label">{template.label}</span>
    </button>
  );
}

function formatReplayDate(tradingDate) {
  return new Date(`${tradingDate}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

function replayCoverageLabel(entry) {
  return Object.entries(entry?.coverage || {})
    .map(([source, value]) => `${source}: ${value?.status || 'unknown'}`)
    .join(', ');
}

function cloneViewConfig(config) {
  return {
    layoutId: config.layoutId,
    panels: config.panels.map((panel) => ({
      ...panel,
      widgets: [...(panel.widgets || [])],
    })),
  };
}

function viewConfigsEqual(left, right) {
  return left.layoutId === right.layoutId
    && left.panels.length === right.panels.length
    && left.panels.every((panel, index) => {
      const other = right.panels[index];
      return panel.id === other?.id
        && panel.activeTab === other.activeTab
        && panel.widgets.length === other.widgets.length
        && panel.widgets.every((widgetId, widgetIndex) => (
          widgetId === other.widgets[widgetIndex]
        ));
    });
}

export default function ConfigModal({
  onClose,
  memberCredentials = null,
  replayDates = [],
  replayDateDiscoveryReady = true,
  onReplayDateSelect = null,
  showResyncChartData = SHOW_RESYNC_CHART_DATA,
}) {
  const widgetList = getAssignableWidgets();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const replayDateDirtyRef = useRef(false);
  const saveOperationRef = useRef(0);
  const saveAbortControllerRef = useRef(null);
  const activeViewId = useDashboardConfig((s) => s.activeViewId);
  const layoutId = useDashboardConfig((s) => s.layoutId);
  const panels = useDashboardConfig((s) => s.panels);
  const viewConfigs = useDashboardConfig((s) => s.viewConfigs);
  const saveConfig = useDashboardConfig((s) => s.saveConfig);
  const replayTradingDate = useDashboardConfig((s) => s.replayTradingDate);
  const replayTradingDateManual = useDashboardConfig((s) => s.replayTradingDateManual);
  const replayHistoryPreloadEnabled = useDashboardConfig(
    (s) => s.replayHistoryPreloadEnabled,
  );
  const settingsViews = DASHBOARD_VIEW_LIST.some((view) => view.id === activeViewId)
    ? DASHBOARD_VIEW_LIST
    : [DASHBOARD_VIEWS[activeViewId], ...DASHBOARD_VIEW_LIST];
  const [editingViewId, setEditingViewId] = useState(activeViewId);
  const [viewSeeds] = useState(() => Object.fromEntries(settingsViews.map((view) => {
    const saved = viewConfigs?.[view.id]
      || (view.id === activeViewId
        ? { layoutId, panels }
        : getDashboardViewDefault(view.id));
    return [view.id, cloneViewConfig(saved)];
  })));
  const [draft, setDraft] = useState(() => ({
    viewConfigs: viewSeeds,
    replayTradingDate,
    replayTradingDateManual,
    replayHistoryPreloadEnabled,
  }));
  const editingViewLabel = DASHBOARD_VIEWS[editingViewId].label;
  const editingViewDraft = draft.viewConfigs[editingViewId];
  const activeReplayDate = replayDates.find(
    (entry) => entry.tradingDate === draft.replayTradingDate,
  ) || null;

  // idle -> confirm -> pending -> (idle | error)
  const [resyncState, setResyncState] = useState('idle');
  const [resyncError, setResyncError] = useState(null);
  const [resyncSummary, setResyncSummary] = useState(null);
  const [replayDateSelecting, setReplayDateSelecting] = useState(false);
  const [replayDateError, setReplayDateError] = useState(null);

  const dismiss = useCallback(() => {
    saveOperationRef.current += 1;
    saveAbortControllerRef.current?.abort();
    saveAbortControllerRef.current = null;
    onClose();
  }, [onClose]);

  useEffect(() => () => {
    saveOperationRef.current += 1;
    saveAbortControllerRef.current?.abort();
    saveAbortControllerRef.current = null;
  }, []);

  useEffect(() => {
    if (replayDateDirtyRef.current) return;
    setDraft((current) => ({
      ...current,
      replayTradingDate,
      replayTradingDateManual,
    }));
  }, [replayTradingDate, replayTradingDateManual]);

  const handleResyncClick = () => {
    if (resyncState === 'idle') {
      setResyncError(null);
      setResyncSummary(null);
      setResyncState('confirm');
      return;
    }
    if (resyncState === 'error') {
      setResyncError(null);
      setResyncSummary(null);
      setResyncState('confirm');
    }
  };

  const handleResyncCancel = () => {
    setResyncState('idle');
    setResyncError(null);
  };

  const handleResyncConfirm = async () => {
    setResyncState('pending');
    const result = await forceResyncChainSnapshots(memberCredentials);
    if (result.ok) {
      setResyncSummary(replayResyncSummary(result));
      setResyncState('idle');
    } else {
      setResyncError(RESYNC_ERROR_MESSAGES[result.reason] || 'Resync failed.');
      setResyncState('error');
    }
  };

  const template = getTemplate(editingViewDraft.layoutId);
  const panelCount = template.panelCount;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    replayDates.forEach((entry) => {
      const coverage = replayCoverageLabel(entry);
      if (coverage) {
        console.debug(`[ConfigModal] replay coverage for ${entry.tradingDate}: ${coverage}`);
      }
    });
  }, [replayDates]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        dismiss();
        return;
      }

      if (e.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not(:disabled), select:not(:disabled), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeIndex = Array.from(focusable).indexOf(document.activeElement);
      if (activeIndex === -1) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  // Visible slots for the current template (pad with placeholders if needed).
  const slots = useMemo(
    () =>
      Array.from({ length: panelCount }, (_, i) =>
        editingViewDraft.panels[i] || { id: `p${i + 1}`, widgets: [], activeTab: null }
      ),
    [editingViewDraft.panels, panelCount]
  );

  // Widgets already placed in a visible panel are "used"; the rest are truly
  // unassigned (shown in the UNASSIGNED section).
  const usedIds = useMemo(() => {
    const set = new Set();
    slots.forEach((p) => (p.widgets || []).forEach((w) => set.add(w)));
    return set;
  }, [slots]);

  const available = widgetList.filter((w) => !usedIds.has(w.id));

  // Map of widget id -> 1-based panel index for widgets currently placed in a
  // visible panel. Used to label "move from Panel N" in the add menus, since a
  // panel's dropdown offers ALL widgets (assigning moves a placed widget here).
  const widgetLocation = useMemo(() => {
    const map = {};
    slots.forEach((p, i) => (p.widgets || []).forEach((w) => { map[w] = i + 1; }));
    return map;
  }, [slots]);

  const handleSelectTemplate = (t) => {
    setDraft((current) => {
      const currentView = current.viewConfigs[editingViewId];
      const nextPanels = currentView.panels.map((panel) => ({
        ...panel,
        widgets: [...(panel.widgets || [])],
      }));
      while (nextPanels.length < t.panelCount) {
        nextPanels.push({
          id: `p${nextPanels.length + 1}`,
          widgets: [],
          activeTab: null,
        });
      }
      return {
        ...current,
        viewConfigs: {
          ...current.viewConfigs,
          [editingViewId]: { layoutId: t.id, panels: nextPanels },
        },
      };
    });
  };

  const handleReplayDateSelect = (event) => {
    const tradingDate = event.target.value;
    if (!tradingDate) return;
    const isLatest = replayDates.some((entry) => (
      entry.tradingDate === tradingDate && entry.isLatest
    ));
    replayDateDirtyRef.current = true;
    setReplayDateError(null);
    setDraft((current) => ({
      ...current,
      replayTradingDate: tradingDate,
      replayTradingDateManual: !isLatest,
    }));
  };

  const handleResetConfig = () => {
    const view = getDashboardViewDefault(editingViewId);

    setDraft((current) => ({
      ...current,
      viewConfigs: {
        ...current.viewConfigs,
        [editingViewId]: cloneViewConfig(view),
      },
    }));
  };

  const handleAssignWidget = (panelId, widgetId) => {
    setDraft((current) => {
      const currentView = current.viewConfigs[editingViewId];
      const nextPanels = currentView.panels.map((panel) => {
        const widgets = panel.widgets.filter((id) => id !== widgetId);
        if (panel.id !== panelId) {
          return {
            ...panel,
            widgets,
            activeTab: widgets.includes(panel.activeTab) ? panel.activeTab : (widgets[0] ?? null),
          };
        }
        const nextWidgets = [...widgets, widgetId];
        return {
          ...panel,
          widgets: nextWidgets,
          activeTab: nextWidgets.includes(panel.activeTab) ? panel.activeTab : widgetId,
        };
      });
      return {
        ...current,
        viewConfigs: {
          ...current.viewConfigs,
          [editingViewId]: { ...currentView, panels: nextPanels },
        },
      };
    });
  };

  const handleRemoveWidget = (panelId, widgetId) => {
    setDraft((current) => {
      const currentView = current.viewConfigs[editingViewId];
      const nextPanels = currentView.panels.map((panel) => {
        if (panel.id !== panelId) return panel;
        const widgets = panel.widgets.filter((id) => id !== widgetId);
        return {
          ...panel,
          widgets,
          activeTab: panel.activeTab === widgetId ? (widgets[0] ?? null) : panel.activeTab,
        };
      });
      return {
        ...current,
        viewConfigs: {
          ...current.viewConfigs,
          [editingViewId]: { ...currentView, panels: nextPanels },
        },
      };
    });
  };

  const handleSave = async () => {
    const saveOperation = saveOperationRef.current + 1;
    saveOperationRef.current = saveOperation;
    const abortController = new AbortController();
    saveAbortControllerRef.current?.abort();
    saveAbortControllerRef.current = abortController;
    const saveWasDismissed = () => (
      abortController.signal.aborted
      || saveOperationRef.current !== saveOperation
    );
    setReplayDateError(null);
    const replayDateChanged = draft.replayTradingDate
      && draft.replayTradingDate !== replayTradingDate;
    if (onReplayDateSelect && replayDateChanged) {
      setReplayDateSelecting(true);
      try {
        const result = await onReplayDateSelect(draft.replayTradingDate, {
          manual: draft.replayTradingDateManual,
          persistSelection: false,
          isCancelled: saveWasDismissed,
          signal: abortController.signal,
        });
        if (saveWasDismissed()) return;
        if (!result?.ok) {
          setReplayDateError(
            RESYNC_ERROR_MESSAGES[result?.reason] || 'Could not load the selected replay date.',
          );
          return;
        }
      } catch (error) {
        if (saveWasDismissed()) return;
        console.error('[ConfigModal] replay date selection failed:', error);
        setReplayDateError('Could not load the selected replay date.');
        return;
      } finally {
        if (!saveWasDismissed()) setReplayDateSelecting(false);
      }
    }

    if (saveWasDismissed()) return;
    if (saveAbortControllerRef.current === abortController) {
      saveAbortControllerRef.current = null;
    }
    const changedViewConfigs = Object.fromEntries(
      Object.entries(draft.viewConfigs).filter(([viewId, config]) => (
        !viewConfigsEqual(config, viewSeeds[viewId])
      )),
    );
    saveConfig({ ...draft, viewConfigs: changedViewConfigs });
    onClose();
  };

  return (
    <div className="cfg-overlay" onClick={dismiss}>
      <div
        ref={dialogRef}
        className="cfg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cfg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cfg-head">
          <span className="cfg-title" id="cfg-title">
            Dashboard Settings - {editingViewLabel}
          </span>
          <button ref={closeRef} type="button" className="cfg-close" onClick={dismiss} aria-label="Close dashboard configuration">
            X
          </button>
        </header>

        <div className="cfg-body">
          <div className="cfg-view-tabs">
            {settingsViews.map((view) => (
              <button
                key={view.id}
                type="button"
                className={`cfg-view-tab${view.id === editingViewId ? ' is-active' : ''}`}
                aria-pressed={view.id === editingViewId}
                onClick={() => setEditingViewId(view.id)}
                disabled={replayDateSelecting}
              >
                {view.label}
              </button>
            ))}
          </div>

          {/* Layout templates */}
          <section className="cfg-section cfg-layout-section">
            <div className="cfg-section-head">
              <div className="cfg-section-label">LAYOUT</div>
              <button
                type="button"
                className="cfg-reset cfg-layout-reset"
                onClick={handleResetConfig}
                disabled={replayDateSelecting}
                aria-busy={replayDateSelecting}
              >
                Reset {editingViewLabel}
              </button>
            </div>
            <div className="cfg-thumbs">
              {TEMPLATE_LIST.map((t) => (
                <TemplateThumb
                  key={t.id}
                  template={t}
                  active={t.id === editingViewDraft.layoutId}
                  disabled={replayDateSelecting}
                  onClick={() => handleSelectTemplate(t)}
                />
              ))}
            </div>
          </section>

          <section className={`cfg-section cfg-replay-date-section${activeReplayDate?.isLatest ? ' cfg-replay-date-section--latest' : ''}`}>
            <div className="cfg-section-label">REPLAY</div>
            <div className="cfg-replay-date-heading">
              <div className="cfg-replay-date-label">ACTIVE DATE</div>
              {draft.replayTradingDate && (
                <div className="cfg-replay-date-status" role="status">
                  {replayDateSelecting ? 'Loading selected replay date...' : formatReplayDate(draft.replayTradingDate)}
                </div>
              )}
              {activeReplayDate && !replayDateSelecting && (
                <span className={`cfg-replay-date-badge${activeReplayDate.isLatest ? ' cfg-replay-date-badge--latest' : ''}`}>
                  {activeReplayDate.isLatest ? 'LATEST' : 'HISTORICAL'}
                </span>
              )}
            </div>
            <select
              className="cfg-add cfg-replay-date-select"
              aria-label="Replay trading date"
              value={replayDates.length ? (draft.replayTradingDate || '') : ''}
              onChange={handleReplayDateSelect}
              disabled={!replayDates.length || replayDateSelecting}
            >
              <option value="">
                {replayDates.length
                  ? 'Choose a replay date...'
                  : replayDateDiscoveryReady
                    ? 'Replay dates unavailable'
                    : 'Loading historical dates...'}
              </option>
              {replayDates.map((entry) => (
                <option key={entry.tradingDate} value={entry.tradingDate}>
                  {formatReplayDate(entry.tradingDate)}
                </option>
              ))}
            </select>
            <label className="cfg-replay-preload">
              <input
                type="checkbox"
                checked={draft.replayHistoryPreloadEnabled}
                disabled={replayDateSelecting}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  replayHistoryPreloadEnabled: event.target.checked,
                }))}
              />
              <span className="cfg-replay-preload-label">Preload historical replay dates?</span>
            </label>
            {replayDateError && <div className="cfg-replay-date-error" role="alert">{replayDateError}</div>}
            {showResyncChartData && (
              <div className="cfg-replay-recovery">
                <div className="cfg-replay-date-label">DATA RECOVERY</div>
                {resyncState === 'confirm' && (
                  <div className="cfg-resync-confirm" role="alertdialog" aria-label="Confirm resync">
                    <span className="cfg-resync-msg">
                      This discards locally-captured chart history and re-fetches from the server. Continue?
                    </span>
                    <button
                      type="button"
                      className="cfg-resync-confirm-btn"
                      onClick={handleResyncConfirm}
                      disabled={replayDateSelecting}
                    >
                      Confirm
                    </button>
                    <button type="button" className="cfg-resync-cancel-btn" onClick={handleResyncCancel}>
                      Cancel
                    </button>
                  </div>
                )}
                {resyncSummary && resyncState === 'idle' && (
                  <div className="cfg-resync-confirm" role="status">
                    <span className="cfg-resync-msg">{resyncSummary}</span>
                  </div>
                )}
                {resyncState === 'error' && (
                  <div className="cfg-resync-confirm" role="alert">
                    <span className="cfg-resync-msg cfg-resync-msg--error">{resyncError}</span>
                    <button
                      type="button"
                      className="cfg-resync-confirm-btn"
                      onClick={handleResyncClick}
                      disabled={replayDateSelecting}
                    >
                      Retry
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="cfg-resync-btn"
                  onClick={handleResyncClick}
                  disabled={
                    replayDateSelecting
                    || !memberCredentials?.memberId
                    || resyncState === 'pending'
                  }
                  aria-busy={resyncState === 'pending'}
                  title={
                    memberCredentials?.memberId
                      ? 'Discard locally-captured chart history and refetch from the server'
                      : 'Not signed in'
                  }
                >
                  {resyncState === 'pending' ? 'Resyncing...' : 'Resync Chart Data'}
                </button>
              </div>
            )}
          </section>

          {/* Panel assignments */}
          <section className="cfg-section cfg-panels-section">
            <div className="cfg-section-label">PANELS</div>
            <div className="cfg-panels">
              {slots.map((panel, i) => (
                <div key={panel.id} className="cfg-panel-card">
                  <div className="cfg-panel-head">
                    <span className="cfg-panel-name">
                      Panel {i + 1}
                      <span className="cfg-panel-area">({AREA_NAMES[i]})</span>
                    </span>
                    {panel.widgets.length > 1 && (
                      <span className="cfg-panel-tabs-note">tabs</span>
                    )}
                  </div>

                  <div className="cfg-chips">
                    {panel.widgets.length === 0 && (
                      <span className="cfg-empty">empty</span>
                    )}
                    {panel.widgets.map((id) => {
                      const w = getWidget(id);
                      return (
                        <span key={id} className="cfg-chip">
                          {w?.label ?? id}
                          <button
                            type="button"
                            className="cfg-chip-x"
                            onClick={() => handleRemoveWidget(panel.id, id)}
                            disabled={replayDateSelecting}
                            title="Remove"
                            aria-label={`Remove ${w?.label ?? id} from Panel ${i + 1}`}
                          >
                            X
                          </button>
                        </span>
                      );
                    })}
                  </div>

                  <select
                    className="cfg-add"
                    value=""
                    aria-label={`Add or move widget to Panel ${i + 1}`}
                    disabled={replayDateSelecting}
                    onChange={(e) => {
                      if (e.target.value) handleAssignWidget(panel.id, e.target.value);
                    }}
                  >
                    <option value="">+ Add / move widget...</option>
                    {widgetList.filter((w) => !panel.widgets.includes(w.id)).map((w) => {
                      const loc = widgetLocation[w.id];
                      return (
                        <option key={w.id} value={w.id}>
                          {w.label}{loc ? ` (move from Panel ${loc})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ))}
            </div>
          </section>

          {available.length > 0 && (
            <section className="cfg-section cfg-unassigned-section">
              <div className="cfg-section-label">UNASSIGNED</div>
              <div className="cfg-chips">
                {available.map((w) => (
                  <span key={w.id} className="cfg-chip cfg-chip--muted">
                    {w.label}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>

        <footer className="cfg-foot">
          <button
            type="button"
            className="cfg-save"
            onClick={() => { void handleSave(); }}
            disabled={replayDateSelecting}
            aria-busy={replayDateSelecting}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
